import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import styled from "styled-components";
import usePutUserInfo from "../../hooks/usePutUserInfo";
import theme from "../../theme";
import {
  MemberDetail,
  MobListForAdmin,
  Permissions,
  SystemSetting,
  User,
} from "../../types/types";
import userStorage from "../../utils/userStorage";
import AuthField from "./authField";
import InputField from "./inputField";
import {
  deleteAdminMemberImage,
  putAdminMemberImage,
} from "../../api/admin";
import AlertModal from "../alertModal";
import { WarningIcon } from "../../assets/icons/icons";
import { Button } from "../common/Button";
import { ModalLayout } from "../common/ModalLayout";
import { useSnackbar } from "notistack";
import { useWindowSize } from "@reactuses/core";
import { useI18n } from "../../locales";
import {
  checkPassword,
  isValidEmail,
  isValidPhone,
} from "../../utils/util";

const { md } = theme.screen;

export interface InfoType {
  id: string;
  pw: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  group_id: string;
  admin_image?: string;
  address_detail?: string;
}
export interface Auths extends Permissions {
  [key: string]: string | number | undefined;
}

const UserModifyModal: React.FC<{
  open: boolean;
  onPressNo: () => void;
  idx: number;
  data?: MemberDetail;
  isMyPage?: boolean;
  systemSetting: SystemSetting | null;
}> = (props) => {
  const { open, onPressNo, idx, data, isMyPage, systemSetting } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { width } = useWindowSize();
  const { t } = useI18n();

  const [info, setInfo] = useState<InfoType>({
    id: "",
    pw: "",
    name: "",
    phone: "",
    email: "",
    address: "",
    group_id: "00000",
    admin_image: "",
    address_detail: "",
  });
  const [selected, setSelected] = useState<
    MobListForAdmin["list"] | null
  >();
  const [selectedAuth, setSelectedAuth] = useState<Auths>();
  const queryClient = useQueryClient();
  const [imgFile, setImgFile] = useState("");
  const [imgData, setImgData] = useState<any>();
  const [imgCancel, setImgCancel] = useState(false);
  const user = userStorage.get();
  const adminImg = data?.admin_image;
  const body = {
    address: info.address,
    address_detail: info.address_detail ? info.address_detail : "",
    email: info.email,
    grade: data?.grade,
    idx,
    mobility_seq: selected?.map((el) => el.seq).join(","),
    name: info.name,
    phone: info.phone,
    pw: info.pw /*? SHA256(info.pw).toString() : ""*/,
    permission: {
      auth_accident: selectedAuth?.auth_accident as string,
      auth_battery: selectedAuth?.auth_battery as string,
      auth_check: selectedAuth?.auth_check as string,
      auth_modify: selectedAuth?.auth_modify as string,
      auth_notice: "Y",
      auth_out: selectedAuth?.auth_out as string,
      auth_warning: selectedAuth?.auth_warning as string,
    },
    group_id: info.group_id,
  };
  const [changeUser, setChangeUser] = useState<User | null>(user);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [pwCheck, setPwCheck] = useState(""); // 비밀번호 확인

  useEffect(() => {
    if (user && changeUser) {
      setChangeUser({
        ...user,
        name: info.name,
        gov_id: info.group_id,
        area: info.group_id,
      });
    }
  }, [info]);

  const { mutate: patchUserInfo, isPending } = usePutUserInfo();

  const handlePatch = () => {
    patchUserInfo(body, {
      onSuccess: async (patchResponse) => {
        let imageSuccess = true; // 이미지 작업 성공 여부
        let imageMessage = ""; // 이미지 작업 메시지

        try {
          if (imgData) {
            // 이미지 수정
            const formData = new FormData();
            formData.append("id", JSON.stringify(info?.id as string));
            formData.append("file", imgData);

            const response = await putAdminMemberImage(formData);

            if (response.code === 2002) {
              if (info.id === user?.id) {
                // 본인일 경우 로컬 스토리지 이미지 업데이트
                localStorage.setItem("image", response.result);
              }
              imageMessage = response.message;
            } else {
              imageSuccess = false;
              imageMessage = response.message;
            }
          } else if (adminImg && !imgData && imgCancel) {
            // 이미지 삭제
            const response = await deleteAdminMemberImage({ idx });

            if (response.code === 2002) {
              localStorage.setItem("image", "");
              imageMessage = response.message;
            } else {
              imageSuccess = false;
              imageMessage = response.message;
            }
          }
        } catch (error) {
          imageSuccess = false;
          imageMessage = "이미지 작업 중 오류가 발생했습니다.";
        }

        // 메시지 처리
        enqueueSnackbar(patchResponse.message, { variant: "success" });

        if (imageMessage) {
          enqueueSnackbar(imageMessage, {
            variant: imageSuccess ? "success" : "error",
          });
        }

        // 최종 정리 및 초기화
        finalizePatch();
      },
    });
  };

  const finalizePatch = () => {
    handleClose();
    setImgData("");
    queryClient.invalidateQueries({ queryKey: ["Member"] });
    queryClient.invalidateQueries({ queryKey: ["MemberDetail"] });

    if (isMyPage && user && user.idx === idx) {
      // 사용자 계정 수정 시 처리
      localStorage.setItem("USER", JSON.stringify(changeUser));
    }
  };

  const handleClose = () => {
    setOpenConfirmModal(false);
    onPressNo();
    setSelected(null);
    setImgFile("");
    setImgData("");
    setInfo({
      id: "",
      pw: "",
      name: "",
      phone: "",
      email: "",
      address: "",
      group_id: "00000",
      admin_image: "",
      address_detail: "",
    });
    setPwCheck("");
  };

  // 필수입력값들
  const isAllFilled =
    [info.id, info.name].every((el) => el !== "") &&
    isValidPhone(info.phone) &&
    isValidEmail(info.email);

  // console.log(info);
  // console.log(pwError);
  // console.log(isAllFilled);

  return (
    <ModalLayout
      open={open}
      onClose={() => setOpenConfirmModal(true)}
      width="1300px"
      title={isMyPage ? t.manageUser.myAccountInfo : t.manageUser.editUser}
    >
      <InputField
        info={info}
        setInfo={setInfo}
        data={data}
        imgFile={imgFile}
        setImgFile={setImgFile}
        imgData={imgData}
        setImgData={setImgData}
        setImgCancel={setImgCancel}
        isMyPage={isMyPage}
        pwCheck={pwCheck}
        setPwCheck={setPwCheck}
      />
      <Title>{t.manageUser.permissionManagement}</Title>
      <AuthField
        selectedAuth={selectedAuth}
        setSelectedAuth={setSelectedAuth}
        data={data}
        isMyPage={isMyPage}
      />
      {/*<Title>계정 기기 연동(선택된 기기 : {selected?.length}대)</Title>
      <DeviceField
        selected={selected}
        setSelected={setSelected}
        data={data}
        idx={idx}
    />*/}
      <BtnWrapper>
        <Button
          onClick={() => handlePatch()}
          bgColor={theme.statusColor.MAIN}
          hoverColor={theme.statusColor.MAIN_HOVER}
          isDisabled={
            !isAllFilled ||
            checkPassword({ pw: info.pw, pwCheck }).pwError ||
            checkPassword({ pw: info.pw, pwCheck }).pwCheckError ||
            !systemSetting?.update
          }
          isLoading={isPending}
        >
          {width <= md ? t.common.save : t.manageUser.editUser}
        </Button>
        <Button
          bgColor={theme.statusColor.CLOSE}
          onClick={() => setOpenConfirmModal(true)}
          hoverColor={theme.statusColor.CLOSE_HOVER}
        >
          {t.common.cancel}
        </Button>
      </BtnWrapper>
      <AlertModal
        open={openConfirmModal}
        onPressNo={() => setOpenConfirmModal(false)}
        title={t.manageUser.cancelRegisterTitle}
        content={t.manageUser.cancelRegisterContent}
        confirmText={t.common.confirm}
        icon={<WarningIcon />}
        onPressYes={handleClose}
      />
    </ModalLayout>
  );
};

const Title = styled.div`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.color.neutral_500};
  padding-bottom: 20px;
  padding-left: 0px;
`;

const BtnWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin: 0 50px;
  margin-top: 20px;

  button {
    width: 270px;
  }

  @media (max-width: ${md}px) {
    margin: 0px;
    margin-top: 20px;
  }
`;

export default UserModifyModal;
